import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Scissors } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { musicApi, errMsg, fmtTime } from "@/lib/musicApi";
import { Waveform } from "@/components/music/Waveform";

const Row = ({ label, value, children }) => (
  <div><div className="mb-1 flex justify-between text-[11px] font-semibold text-white/50"><span>{label}</span><span className="font-mono text-[#C8FF4D]">{value}</span></div>{children}</div>
);

// Non-destructive FFmpeg edit → saved as a new version.
export const EditDialog = ({ t, onClose, onDone }) => {
  const dur = t.duration || 0;
  const [e, setE] = useState({ trim_start: 0, trim_end: dur, fade_in: 0, fade_out: 0, speed: 1, normalize: true });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setE((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { onDone(await musicApi.edit(t.id, { ...e, trim_end: e.trim_end >= dur ? null : e.trim_end })); toast.success("Saved as a new version"); }
    catch (err) { toast.error(errMsg(err, "Edit failed")); }
    setBusy(false);
  };
  const range = (k, min, max, step) => <input type="range" min={min} max={max} step={step} value={e[k]} onChange={(ev) => set(k, Number(ev.target.value))} data-testid={`edit-${k}`} className="w-full accent-[#C8FF4D]" />;
  const s = dur ? e.trim_start / dur : 0, en = dur ? e.trim_end / dur : 1;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-white/10 bg-[#101010] text-white" data-testid="music-edit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Scissors size={16} className="text-[#C8FF4D]" /> Edit “{t.title}”</DialogTitle></DialogHeader>
        <div className="relative rounded-lg bg-black/50 p-2">
          <Waveform peaks={t.peaks} progress={1} height={48} />
          <div className="pointer-events-none absolute inset-y-2 left-2 bg-black/70" style={{ width: `calc(${s * 100}% - ${s * 16}px)` }} />
          <div className="pointer-events-none absolute inset-y-2 right-2 bg-black/70" style={{ width: `calc(${(1 - en) * 100}% - ${(1 - en) * 16}px)` }} />
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Row label="Start" value={fmtTime(e.trim_start)}>{range("trim_start", 0, Math.max(0, e.trim_end - 1), 0.5)}</Row>
            <Row label="End" value={fmtTime(e.trim_end)}>{range("trim_end", e.trim_start + 1, dur, 0.5)}</Row>
            <Row label="Fade in" value={`${e.fade_in}s`}>{range("fade_in", 0, 10, 0.5)}</Row>
            <Row label="Fade out" value={`${e.fade_out}s`}>{range("fade_out", 0, 10, 0.5)}</Row>
          </div>
          <Row label="Speed (tempo)" value={`${e.speed.toFixed(2)}×`}>{range("speed", 0.5, 2, 0.05)}</Row>
          <label className="flex items-center gap-2 text-xs text-white/70"><input type="checkbox" checked={e.normalize} onChange={(ev) => set("normalize", ev.target.checked)} data-testid="edit-normalize" className="accent-[#C8FF4D]" /> Normalize loudness (−14 LUFS, streaming-ready)</label>
          <div className="flex justify-between text-[11px] text-white/40"><span>Result ≈ {fmtTime((e.trim_end - e.trim_start) / e.speed)}</span><span>Original is kept</span></div>
          <button onClick={save} disabled={busy} data-testid="edit-save" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#C8FF4D] py-2.5 text-sm font-black text-black disabled:opacity-50">
            {busy && <Loader2 size={15} className="animate-spin" />} Save as new version
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
